import { PreApprovalData, PreApprovalAnalytics, PreApprovalListItem } from '@/types/pre-approval';
import { supabase } from '@/utils/supabase';
import { getCurrentUserProfile } from '@/utils/profileUtils';
import {
  formatPreApprovalExpirationDateInput,
  isPreApprovalExpired,
  resolveCanonicalPreApprovalExpiresAt
} from '@/utils/pre-approval/expiration';

import logger from '@/utils/logger';
export interface DatabasePreApproval {
  id: string;
  organization_id: string;
  template_id?: string;
  loan_journey_id?: string;
  prospect_id?: string;
  borrower_id?: string;
  contact_id?: string;
  created_by_user_id: string;
  borrower_info: any;
  co_borrower_info?: any;
  loan_details: any;
  property_info?: any;
  custom_fields?: any;
  status: 'draft' | 'sent' | 'viewed' | 'accepted' | 'expired';
  expires_at?: string;
  expired_at?: string;
  expired_reason?: 'manual' | 'time_expired' | 'superseded_by_new';
  sent_at?: string;
  viewed_at?: string;
  accepted_at?: string;
  view_count: number;
  share_token?: string;
  share_expires_at?: string;
  tracking_id?: string;
  modifications?: unknown[];
  comments?: unknown[];
  metadata?: any;
  deleted_at?: string;
  created_at: string;
  updated_at: string;
  template_data?: any; // Visual template data
  source?: string; // Origin: 'web', 'wisr_sms', 'api', etc.

  // E-Signature fields
  loan_officer_signature_date?: string;
  loan_officer_signature_data_url?: string;
  loan_officer_signature_method?: 'drawn' | 'typed';
  loan_officer_signature_font?: string;
  loan_officer_signature_ip_address?: string;
  loan_officer_signature_user_agent?: string;
  loan_officer_signature_audit_log?: any;
  has_loan_officer_signature?: boolean;

  // Signature tracking
  signature_send_count?: number;
  last_sent_with_signature_at?: string;
  signature_recipient_history?: any[];

  // Signed PDF
  signed_pdf_url?: string;
  signed_pdf_generated_at?: string;
}

export class SupabasePreApprovalService {
  // Convert from database format to app format
  private static convertFromDatabase(dbPreApproval: DatabasePreApproval): PreApprovalData {
    return {
      id: dbPreApproval.id,
      templateId: dbPreApproval.template_id || '',
      loanOfficerId: dbPreApproval.created_by_user_id,
      borrowerInfo: dbPreApproval.borrower_info || {},
      coBorrowerInfo: dbPreApproval.co_borrower_info || undefined,
      loanDetails: dbPreApproval.loan_details || {},
      propertyInfo: dbPreApproval.property_info || {},
      customFields: dbPreApproval.custom_fields || {},
      status: dbPreApproval.status,
      createdAt: dbPreApproval.created_at,
      updatedAt: dbPreApproval.updated_at,
      sentAt: dbPreApproval.sent_at,
      viewedAt: dbPreApproval.viewed_at,
      acceptedAt: dbPreApproval.accepted_at,
      expiresAt: dbPreApproval.expires_at,
      expirationDate: formatPreApprovalExpirationDateInput(dbPreApproval.expires_at),
      expiredAt: dbPreApproval.expired_at,
      expiredReason: dbPreApproval.expired_reason,
      trackingId: dbPreApproval.tracking_id,
      // Note: view_count column is deprecated - using event-based tracking now
      // For accurate counts, use getViewCount() method
      viewCount: dbPreApproval.view_count || 0,
      modifications: dbPreApproval.modifications || [],
      comments: dbPreApproval.comments || [],
      template_data: dbPreApproval.template_data || null,
      metadata: dbPreApproval.metadata || {},
      source: dbPreApproval.source || 'web',

      // E-Signature fields
      loanOfficerSignatureDate: dbPreApproval.loan_officer_signature_date,
      loanOfficerSignatureDataUrl: dbPreApproval.loan_officer_signature_data_url,
      loanOfficerSignatureMethod: dbPreApproval.loan_officer_signature_method,
      loanOfficerSignatureFont: dbPreApproval.loan_officer_signature_font,
      loanOfficerSignatureIpAddress: dbPreApproval.loan_officer_signature_ip_address,
      loanOfficerSignatureUserAgent: dbPreApproval.loan_officer_signature_user_agent,
      loanOfficerSignatureAuditLog: dbPreApproval.loan_officer_signature_audit_log,
      hasLoanOfficerSignature: dbPreApproval.has_loan_officer_signature,

      // Signature tracking
      signatureSendCount: dbPreApproval.signature_send_count,
      lastSentWithSignatureAt: dbPreApproval.last_sent_with_signature_at,
      signatureRecipientHistory: dbPreApproval.signature_recipient_history,

      // Signed PDF
      signedPdfUrl: dbPreApproval.signed_pdf_url,
      signedPdfGeneratedAt: dbPreApproval.signed_pdf_generated_at,

      // Contact linking
      contactId: dbPreApproval.contact_id,
      accessLink: dbPreApproval.share_token ? {
        token: dbPreApproval.share_token,
        url: `/pre-approval/${dbPreApproval.share_token}`,
        expiresAt: dbPreApproval.share_expires_at || dbPreApproval.expires_at || '',
        lastAccessed: null
      } : undefined
    };
  }

  // Convert from app format to database format
  private static convertToDatabase(preApproval: PreApprovalData): Partial<DatabasePreApproval> {
    return {
      template_id: preApproval.templateId || null,
      borrower_info: preApproval.borrowerInfo,
      co_borrower_info: preApproval.coBorrowerInfo || null,
      loan_details: preApproval.loanDetails,
      property_info: preApproval.propertyInfo,
      custom_fields: preApproval.customFields,
      status: preApproval.status,
      contact_id: preApproval.contactId || null,
      expires_at: resolveCanonicalPreApprovalExpiresAt(preApproval.expiresAt, preApproval.expirationDate),
      expired_at: preApproval.expiredAt,
      expired_reason: preApproval.expiredReason,
      sent_at: preApproval.sentAt,
      viewed_at: preApproval.viewedAt,
      accepted_at: preApproval.acceptedAt,
      view_count: preApproval.viewCount || 0,
      tracking_id: preApproval.trackingId,
      modifications: preApproval.modifications || [],
      comments: preApproval.comments || [],
      metadata: preApproval.metadata || {},
      template_data: preApproval.template_data || null,
      share_token: preApproval.accessLink?.token,
      share_expires_at: preApproval.accessLink?.expiresAt,

      // E-Signature fields
      loan_officer_signature_date: preApproval.loanOfficerSignatureDate,
      loan_officer_signature_data_url: preApproval.loanOfficerSignatureDataUrl,
      loan_officer_signature_method: preApproval.loanOfficerSignatureMethod,
      loan_officer_signature_font: preApproval.loanOfficerSignatureFont,
      loan_officer_signature_ip_address: preApproval.loanOfficerSignatureIpAddress,
      loan_officer_signature_user_agent: preApproval.loanOfficerSignatureUserAgent,
      loan_officer_signature_audit_log: preApproval.loanOfficerSignatureAuditLog,

      // Signature tracking
      signature_send_count: preApproval.signatureSendCount,
      last_sent_with_signature_at: preApproval.lastSentWithSignatureAt,
      signature_recipient_history: preApproval.signatureRecipientHistory,

      // Signed PDF
      signed_pdf_url: preApproval.signedPdfUrl,
      signed_pdf_generated_at: preApproval.signedPdfGeneratedAt
    };
  }

  /**
   * Resolves a pre-approval ID or share token to a UUID.
   * This helper ensures all database operations use UUIDs while
   * allowing public methods to accept both UUIDs and share tokens.
   *
   * @param idOrToken - Either a UUID or a share token
   * @returns The UUID if found, null otherwise
   */
  private static async resolvePreApprovalId(idOrToken: string): Promise<string | null> {
    try {
      // UUID format: 8-4-4-4-12 hex characters with hyphens
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(idOrToken);

      // If already a UUID, return immediately (zero overhead for authenticated users)
      if (isUUID) {
        return idOrToken;
      }

      // It's a token - look up the UUID
      logger.info('Resolving share token to UUID', {
        token: `${idOrToken.substring(0, 20)  }...`
      });

      const { data, error } = await supabase
        .from('pre_approvals')
        .select('id')
        .eq('share_token', idOrToken)
        .single();

      if (error) {
        logger.error('Failed to resolve token to UUID', { error: error.message, code: error.code });
        return null;
      }

      if (!data) {
        logger.warn('No pre-approval found for token');
        return null;
      }

      logger.info('Token resolved to UUID', { uuid: data.id });
      return data.id;
    } catch (error) {
      logger.error('Error in resolvePreApprovalId', error);
      return null;
    }
  }

  // Get all pre-approvals for the current user
  static async getAllPreApprovals(loanOfficerId?: string): Promise<PreApprovalData[]> {
    try {
      // Use getCurrentUserProfile to respect impersonation
      const profile = await getCurrentUserProfile();
      logger.info('Fetching pre-approvals for user:', profile.email);

      let query = supabase
        .from('pre_approvals')
        .select(`
          *,
          created_by:users!pre_approvals_created_by_user_id_fkey(
            id,
            email,
            full_name
          )
        `)
        .order('created_at', { ascending: false })
        .is('deleted_at', null); // Filter out soft-deleted records

      // Filter by the effective user (impersonated or actual)
      const effectiveUserId = loanOfficerId || profile.id;
      query = query.eq('created_by_user_id', effectiveUserId);

      const { data, error } = await query;

      if (error) {
        logger.error('Error fetching pre-approvals from Supabase:', error);
        throw new Error(`Failed to fetch pre-approvals: ${error.message}`);
      }

      // Validate and filter out corrupted records with defensive error handling
      const validPreApprovals: any[] = [];
      const corruptedIds: string[] = [];

      (data || []).forEach(preApproval => {
        try {
          // Attempt to access JSONB fields - this will throw if JSON is malformed
          const testBorrower = preApproval.borrower_info;
          const testLoan = preApproval.loan_details;
          const testProperty = preApproval.property_info;
          const testTemplate = preApproval.template_data;
          const testMetadata = preApproval.metadata;
          const testModifications = preApproval.modifications;
          const testComments = preApproval.comments;

          // Try to stringify to ensure no circular references or other issues
          JSON.stringify({
            borrower_info: testBorrower,
            loan_details: testLoan,
            property_info: testProperty,
            template_data: testTemplate,
            metadata: testMetadata
          });

          // If we got here, the record is valid
          validPreApprovals.push(preApproval);
        } catch (parseError) {
          // Record is corrupted - log and skip
          const recordId = preApproval.id || 'unknown';
          corruptedIds.push(recordId);
          logger.error('Corrupted pre-approval record detected and skipped', {
            id: recordId,
            error: parseError instanceof Error ? parseError.message : String(parseError),
            borrowerName: preApproval.borrower_info?.firstName || 'Unknown'
          });
        }
      });

      // Warn if corrupted records were found
      if (corruptedIds.length > 0) {
        logger.warn(`Skipped ${corruptedIds.length} corrupted pre-approval record(s)`, {
          ids: corruptedIds,
          message: 'These records contain malformed JSON data and cannot be loaded. Please check the database or contact support.'
        });
      }

      // Check for expired pre-approvals and update their status
      const expiredAt = new Date().toISOString();
      const expiredPreApprovals = validPreApprovals.filter(preApproval => {
        if (['sent', 'viewed', 'accepted'].includes(preApproval.status) && preApproval.expires_at) {
          return isPreApprovalExpired(preApproval.expires_at);
        }
        return false;
      });

      // Update expired pre-approvals in database
      if (expiredPreApprovals.length > 0) {
        await Promise.all(
          expiredPreApprovals.map(preApproval =>
            supabase
              .from('pre_approvals')
              .update({
                status: 'expired',
                expired_at: expiredAt,
                expired_reason: 'time_expired'
              })
              .eq('id', preApproval.id)
          )
        );
      }

      // Convert and map loan officer data (using validPreApprovals only)
      return validPreApprovals.map(preApproval => {
        const converted = this.convertFromDatabase(preApproval);
        if (preApproval.created_by) {
          converted.loanOfficerId = preApproval.created_by.id;
        }
        // Update status in memory if expired
        if (expiredPreApprovals.some(pa => pa.id === preApproval.id)) {
          converted.status = 'expired';
        }
        return converted;
      });
    } catch (error) {
      logger.error('Error in getAllPreApprovals:', error);
      throw error;
    }
  }

  // Save or update a pre-approval
  static async savePreApproval(preApproval: PreApprovalData): Promise<string> {
    logger.info('Starting Supabase save for pre-approval:', preApproval.id);

    try {
      // Check authentication
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) {
        logger.error('Auth error:', authError);
        throw new Error(`Authentication failed: ${authError.message}`);
      }
      if (!user) {
throw new Error('Not authenticated - please log in');
}

      // Get user's profile with automatic repair
      const profile = await getCurrentUserProfile();

      if (!profile.organization_id) {
        throw new Error('User profile missing organization_id. Please refresh the page and try again.');
      }

      logger.info('Profile successfully fetched:', {
        userId: user.id,
        email: profile.email,
        organizationId: profile.organization_id
      });

      logger.info('=== convertToDatabase Debug ===');
      logger.info('Input preApproval has template_data?', !!preApproval.template_data);
      logger.info('Template data elements count:', preApproval.template_data?.elements?.length);
      
      const dbPreApproval = this.convertToDatabase(preApproval);
      
      logger.info('Converted dbPreApproval has template_data?', !!dbPreApproval.template_data);
      logger.info('DB template data:', dbPreApproval.template_data);

      // Check if this is a valid UUID (existing pre-approval from database)
      const isValidUUID = preApproval.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(preApproval.id);

      if (isValidUUID) {
        // Check if pre-approval exists in database
        const { data: existingPreApproval, error: checkError } = await supabase
          .from('pre_approvals')
          .select('id')
          .eq('id', preApproval.id)
          .single();

        if (checkError && checkError.code === 'PGRST116') {
          // Pre-approval doesn't exist in database, insert it
          logger.info('UUID pre-approval not found in database, inserting as new');
          const insertData = {
            ...dbPreApproval,
            id: preApproval.id, // Keep the UUID
            created_by_user_id: profile.id, // Use profile.id to respect impersonation
            organization_id: profile.organization_id
          };

          const { error: insertError } = await supabase
            .from('pre_approvals')
            .insert(insertData);

          if (insertError) {
            logger.error('Insert error:', insertError);
            throw new Error(`Failed to insert pre-approval: ${insertError.message}`);
          }
        } else if (!checkError && existingPreApproval) {
          // Pre-approval exists, update it
          const updateData = {
            ...dbPreApproval,
            updated_at: new Date().toISOString()
          };

          const { error: updateError } = await supabase
            .from('pre_approvals')
            .update(updateData)
            .eq('id', preApproval.id);

          if (updateError) {
            logger.error('Update error:', updateError);
            throw new Error(`Failed to update pre-approval: ${updateError.message}`);
          }
        } else if (checkError) {
          throw new Error(`Failed to check pre-approval existence: ${checkError.message}`);
        }
      } else {
        // Non-UUID ID - insert as new with generated UUID
        logger.info('Non-UUID ID detected, creating new pre-approval in Supabase');
        const insertData = {
          ...dbPreApproval,
          created_by_user_id: profile.id,
          organization_id: profile.organization_id
        };

        const { data, error } = await supabase
          .from('pre_approvals')
          .insert(insertData)
          .select()
          .single();

        if (error) {
          logger.error('Insert error:', error);
          throw new Error(`Failed to insert pre-approval: ${error.message}`);
        }

        // Update the pre-approval ID with the database-generated ID
        if (data) {
          preApproval.id = data.id;
          logger.info(`Pre-approval saved with new ID: ${data.id}`);
          return data.id;
        }
      }
      
      // Return the pre-approval ID (either existing or newly created)
      return preApproval.id;
    } catch (error) {
      logger.error('Error saving pre-approval to Supabase:', error);
      throw error;
    }
  }

  // Get a specific pre-approval by ID or share token
  static async getPreApprovalById(idOrToken: string): Promise<PreApprovalData | null> {
    try {
      // Detect if input is UUID or share token
      // UUID format: 8-4-4-4-12 hex characters with hyphens
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(idOrToken);
      const column = isUUID ? 'id' : 'share_token';

      logger.info('Fetching pre-approval', {
        input: `${idOrToken.substring(0, 20)  }...`,
        detectedType: isUUID ? 'UUID' : 'TOKEN',
        queryColumn: column
      });

      // Check if user is authenticated
      const { data: { user } } = await supabase.auth.getUser();

      if (!user && !isUUID) {
        return this.getPublicAccessiblePreApproval(idOrToken);
      }

      let query;

      if (user) {
        // Authenticated query - include profile join
        query = supabase
          .from('pre_approvals')
          .select(`
            *,
            created_by:users!pre_approvals_created_by_user_id_fkey(
              id,
              email,
              full_name
            )
          `)
          .eq(column, idOrToken)
          .is('deleted_at', null); // Filter out soft-deleted records
      } else {
        // Unauthenticated query - simpler, relies on public RLS policy
        logger.info('Fetching pre-approval as unauthenticated user (public access)');
        query = supabase
          .from('pre_approvals')
          .select('*')
          .eq(column, idOrToken)
          .is('deleted_at', null); // Filter out soft-deleted records
      }

      const { data, error } = await query.single();

      if (error) {
        logger.error('Error fetching pre-approval:', error);
        if (error.code === 'PGRST116') {
          logger.info('Pre-approval not found in share_token field');

          logger.info('Pre-approval not found or not accessible with current permissions');
          return null;
        }
        throw new Error(`Failed to fetch pre-approval: ${error.message}`);
      }

      if (!data) {
        return null;
      }

      const converted = this.convertFromDatabase(data);
      if (data.created_by) {
        converted.loanOfficerId = data.created_by.id;
      } else if (data.created_by_user_id) {
        // For public access, we might not have the profile join
        converted.loanOfficerId = data.created_by_user_id;
      }

      return converted;
    } catch (error) {
      logger.error('Error fetching pre-approval from Supabase:', error);
      throw error;
    }
  }

  static async getPublicAccessiblePreApproval(token: string): Promise<PreApprovalData | null> {
    try {
      const { data, error } = await supabase.functions.invoke('public-preapproval-access', {
        body: { token }
      });

      if (error) {
        logger.error('Public pre-approval access failed:', error);
        return null;
      }

      if (!data?.success || !data.preApproval) {
        logger.warn('Public pre-approval access rejected', {
          token: `${token.substring(0, 8)}...`,
          reason: data?.error
        });
        return null;
      }

      const publicPreApproval: DatabasePreApproval = {
        organization_id: '',
        created_by_user_id: '',
        status: 'sent',
        view_count: 0,
        created_at: '',
        updated_at: '',
        ...data.preApproval
      };
      const converted = this.convertFromDatabase(publicPreApproval);
      converted.loanOfficerId = '';
      converted.publicProfileVariables = data.profileVariables || undefined;

      return converted;
    } catch (error) {
      logger.error('Error in getPublicAccessiblePreApproval:', error);
      return null;
    }
  }

  // Delete a pre-approval (SOFT DELETE)
  static async deletePreApproval(idOrToken: string): Promise<void> {
    try {
      // Resolve token to UUID
      const id = await this.resolvePreApprovalId(idOrToken);
      if (!id) {
        throw new Error('Pre-approval not found');
      }

      logger.info('Soft deleting pre-approval from Supabase:', id);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
throw new Error('Not authenticated');
}

      // Perform soft delete by setting deleted_at timestamp
      const { error } = await supabase
        .from('pre_approvals')
        .update({
          deleted_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('created_by_user_id', user.id)
        .is('deleted_at', null); // Only delete if not already deleted

      if (error) {
        logger.error('Error soft deleting pre-approval:', error);
        throw new Error(`Failed to delete pre-approval: ${error.message}`);
      }

      logger.info('Pre-approval soft deleted successfully from Supabase');
    } catch (error) {
      logger.error('Error in deletePreApproval:', error);
      throw error;
    }
  }

  /**
   * @deprecated Send flows must not auto-expire older pre-approvals. Keep this
   * only for historical maintenance until all references are removed.
   * @param borrowerEmail - The borrower's email to match
   * @param userId - The loan officer's user ID (only expires their own pre-approvals)
   * @param excludeId - Optional ID to exclude from expiration (the new pre-approval being sent)
   * @returns Number of pre-approvals that were expired
   */
  static async expirePreviousPreApprovals(
    borrowerEmail: string,
    userId: string,
    excludeId?: string
  ): Promise<number> {
    try {
      if (!borrowerEmail) {
        logger.warn('expirePreviousPreApprovals called without borrower email');
        return 0;
      }

      logger.info('Auto-expiring previous pre-approvals for borrower:', {
        borrowerEmail,
        userId,
        excludeId
      });

      // Build the query to find active pre-approvals for this borrower
      let query = supabase
        .from('pre_approvals')
        .update({
          status: 'expired',
          expired_at: new Date().toISOString(),
          expired_reason: 'superseded_by_new',
          updated_at: new Date().toISOString()
        })
        .eq('created_by_user_id', userId)
        .in('status', ['sent', 'viewed'])
        .is('deleted_at', null)
        .filter('borrower_info->>email', 'ilike', borrowerEmail);

      // Exclude the current pre-approval if specified
      if (excludeId) {
        query = query.neq('id', excludeId);
      }

      const { data, error } = await query.select('id');

      if (error) {
        logger.error('Failed to expire previous pre-approvals:', error);
        throw new Error(`Failed to expire previous pre-approvals: ${error.message}`);
      }

      const expiredCount = data?.length || 0;

      if (expiredCount > 0) {
        logger.info(`Successfully auto-expired ${expiredCount} previous pre-approval(s) for ${borrowerEmail}`);
      }

      return expiredCount;
    } catch (error) {
      logger.error('Error in expirePreviousPreApprovals:', error);
      throw error;
    }
  }

  // Record pre-approval view (for consumer tracking)
  static async recordPreApprovalView(idOrToken: string): Promise<void> {
    try {
      // Resolve token to UUID
      const id = await this.resolvePreApprovalId(idOrToken);
      if (!id) {
        throw new Error('Pre-approval not found');
      }

      logger.info('Recording pre-approval view for ID:', id);

      // First fetch the current pre-approval data
      const { data: preApproval, error: fetchError } = await supabase
        .from('pre_approvals')
        .select('view_count, viewed_at, status')
        .eq('id', id)
        .single();

      if (fetchError) {
        logger.error('Error fetching pre-approval for view tracking:', fetchError);
        // Continue anyway - we'll try to update
      }

      // Update view count and first viewed time
      const updates: {
        view_count: number;
        viewed_at?: string;
        status?: string;
      } = {
        view_count: (preApproval?.view_count || 0) + 1
      };

      // Only update viewed_at and status if not already viewed
      if (!preApproval?.viewed_at) {
        updates.viewed_at = new Date().toISOString();
        updates.status = 'viewed';
      }

      const { error: updateError } = await supabase
        .from('pre_approvals')
        .update(updates)
        .eq('id', id);

      if (updateError) {
        logger.error('Error updating pre-approval view:', updateError);
        throw new Error(`Failed to record pre-approval view: ${updateError.message}`);
      }

      logger.info('Pre-approval view recorded successfully');
    } catch (error) {
      logger.error('Error recording pre-approval view in Supabase:', error);
      throw error;
    }
  }

  // Get analytics
  static async getAnalytics(): Promise<PreApprovalAnalytics> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
throw new Error('Not authenticated');
}

      // Get user's profile with automatic repair
      const profile = await getCurrentUserProfile();
      logger.info('Getting pre-approval analytics for user:', profile.email);

      if (!profile.organization_id) {
        throw new Error('No organization found. Please refresh the page and try again.');
      }

      // Get all pre-approvals for the effective user (respects impersonation)
      const { data: preApprovals, error } = await supabase
        .from('pre_approvals')
        .select('status, created_at, sent_at, viewed_at, accepted_at')
        .eq('created_by_user_id', profile.id)
        .is('deleted_at', null); // Filter out soft-deleted records

      if (error) {
        logger.error('Error fetching pre-approval analytics:', error);
        throw new Error(`Failed to fetch analytics: ${error.message}`);
      }

      // Calculate analytics
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      const analytics = {
        totalPreApprovals: preApprovals?.length || 0,
        totalPreApprovalsThisMonth: preApprovals?.filter(pa => 
          new Date(pa.created_at) >= startOfMonth
        ).length || 0,
        preApprovalsSent: preApprovals?.filter(pa => 
          pa.status === 'sent' || pa.status === 'viewed' || pa.status === 'accepted'
        ).length || 0,
        preApprovalsViewed: preApprovals?.filter(pa => 
          pa.status === 'viewed' || pa.status === 'accepted'
        ).length || 0,
        preApprovalsAccepted: preApprovals?.filter(pa => 
          pa.status === 'accepted'
        ).length || 0,
        viewRate: 0,
        acceptanceRate: 0,
        averageTimeToView: 0,
        averageTimeToAccept: 0
      };

      // Calculate view rate
      if (analytics.preApprovalsSent > 0) {
        analytics.viewRate = Math.round((analytics.preApprovalsViewed / analytics.preApprovalsSent) * 100);
      }

      // Calculate acceptance rate
      if (analytics.preApprovalsViewed > 0) {
        analytics.acceptanceRate = Math.round((analytics.preApprovalsAccepted / analytics.preApprovalsViewed) * 100);
      }

      // Calculate average time to view
      const viewedPreApprovals = preApprovals?.filter(pa => pa.sent_at && pa.viewed_at) || [];
      if (viewedPreApprovals.length > 0) {
        const totalHours = viewedPreApprovals.reduce((sum, pa) => {
          const sentTime = new Date(pa.sent_at!).getTime();
          const viewTime = new Date(pa.viewed_at!).getTime();
          return sum + ((viewTime - sentTime) / (1000 * 60 * 60));
        }, 0);
        analytics.averageTimeToView = Math.round(totalHours / viewedPreApprovals.length * 10) / 10;
      }

      // Calculate average time to accept
      const acceptedPreApprovals = preApprovals?.filter(pa => pa.sent_at && pa.accepted_at) || [];
      if (acceptedPreApprovals.length > 0) {
        const totalHours = acceptedPreApprovals.reduce((sum, pa) => {
          const sentTime = new Date(pa.sent_at!).getTime();
          const acceptTime = new Date(pa.accepted_at!).getTime();
          return sum + ((acceptTime - sentTime) / (1000 * 60 * 60));
        }, 0);
        analytics.averageTimeToAccept = Math.round(totalHours / acceptedPreApprovals.length * 10) / 10;
      }

      return analytics;
    } catch (error) {
      logger.error('Error fetching pre-approval analytics from Supabase:', error);
      throw error;
    }
  }

  // Update pre-approval status
  static async updatePreApprovalStatus(
    idOrToken: string,
    status: PreApprovalData['status'],
    additionalData?: Partial<PreApprovalData>
  ): Promise<void> {
    try {
      // Resolve token to UUID
      const id = await this.resolvePreApprovalId(idOrToken);
      if (!id) {
        throw new Error('Pre-approval not found');
      }

      const updates: any = {
        status,
        updated_at: new Date().toISOString()
      };

      // Update timestamps based on status
      if (status === 'sent' && additionalData?.sentAt) {
        updates.sent_at = additionalData.sentAt;
      } else if (status === 'viewed' && additionalData?.viewedAt) {
        updates.viewed_at = additionalData.viewedAt;
        updates.view_count = (additionalData.viewCount || 0);
      } else if (status === 'accepted' && additionalData?.acceptedAt) {
        updates.accepted_at = additionalData.acceptedAt;
      }

      // Apply any additional data
      if (additionalData?.expiresAt) {
        updates.expires_at = additionalData.expiresAt;
      }
      if (additionalData?.trackingId) {
        updates.tracking_id = additionalData.trackingId;
      }

      const { error } = await supabase
        .from('pre_approvals')
        .update(updates)
        .eq('id', id);

      if (error) {
        logger.error('Error updating pre-approval status:', error);
        throw new Error(`Failed to update pre-approval status: ${error.message}`);
      }

      logger.info('Pre-approval status updated successfully');
    } catch (error) {
      logger.error('Error in updatePreApprovalStatus:', error);
      throw error;
    }
  }

  // Update pre-approval with any fields
  static async updatePreApproval(
    idOrToken: string,
    updates: Partial<PreApprovalData>
  ): Promise<PreApprovalData | null> {
    try {
      // Resolve token to UUID
      const id = await this.resolvePreApprovalId(idOrToken);
      if (!id) {
        throw new Error('Pre-approval not found');
      }

      const profile = await getCurrentUserProfile();
      logger.info('Updating pre-approval:', id);

      // Convert updates to database format
      const dbUpdates: Partial<DatabasePreApproval> = {};

      if (updates.status !== undefined) {
        dbUpdates.status = updates.status;
      }
      if (updates.borrowerInfo !== undefined) {
        dbUpdates.borrower_info = updates.borrowerInfo;
      }
      if ('coBorrowerInfo' in updates) {
        dbUpdates.co_borrower_info = updates.coBorrowerInfo || null;
      }
      if (updates.loanDetails !== undefined) {
        dbUpdates.loan_details = updates.loanDetails;
      }
      if (updates.propertyInfo !== undefined) {
        dbUpdates.property_info = updates.propertyInfo;
      }
      if (updates.customFields !== undefined) {
        dbUpdates.custom_fields = updates.customFields;
      }
      if (updates.contactId !== undefined) {
        dbUpdates.contact_id = updates.contactId;
      }
      if (updates.expiresAt !== undefined) {
        dbUpdates.expires_at = updates.expiresAt;
      }
      if (updates.expirationDate !== undefined && updates.expiresAt === undefined) {
        dbUpdates.expires_at = updates.expirationDate;
      }
      if (updates.expiredAt !== undefined) {
        dbUpdates.expired_at = updates.expiredAt;
      }
      if (updates.expiredReason !== undefined) {
        dbUpdates.expired_reason = updates.expiredReason;
      }
      if (updates.sentAt !== undefined) {
        dbUpdates.sent_at = updates.sentAt;
      }
      if (updates.viewedAt !== undefined) {
        dbUpdates.viewed_at = updates.viewedAt;
      }
      if (updates.acceptedAt !== undefined) {
        dbUpdates.accepted_at = updates.acceptedAt;
      }
      if (updates.viewCount !== undefined) {
        dbUpdates.view_count = updates.viewCount;
      }
      if (updates.trackingId !== undefined) {
        dbUpdates.tracking_id = updates.trackingId;
      }
      if (updates.modifications !== undefined) {
        dbUpdates.modifications = updates.modifications;
      }
      if (updates.comments !== undefined) {
        dbUpdates.comments = updates.comments;
      }

      // Handle special fields
      if (updates.lastSentAt !== undefined) {
        dbUpdates.sent_at = updates.lastSentAt;
      }
      if (updates.accessLink !== undefined) {
        dbUpdates.share_token = updates.accessLink.token;
        dbUpdates.share_expires_at = updates.accessLink.expiresAt;
      }

      // E-Signature field mappings
      if (updates.loanOfficerSignatureDate !== undefined) {
        dbUpdates.loan_officer_signature_date = updates.loanOfficerSignatureDate;
      }
      if (updates.loanOfficerSignatureDataUrl !== undefined) {
        dbUpdates.loan_officer_signature_data_url = updates.loanOfficerSignatureDataUrl;
      }
      if (updates.loanOfficerSignatureMethod !== undefined) {
        dbUpdates.loan_officer_signature_method = updates.loanOfficerSignatureMethod;
      }
      if (updates.loanOfficerSignatureFont !== undefined) {
        dbUpdates.loan_officer_signature_font = updates.loanOfficerSignatureFont;
      }
      if (updates.loanOfficerSignatureIpAddress !== undefined) {
        dbUpdates.loan_officer_signature_ip_address = updates.loanOfficerSignatureIpAddress;
      }
      if (updates.loanOfficerSignatureUserAgent !== undefined) {
        dbUpdates.loan_officer_signature_user_agent = updates.loanOfficerSignatureUserAgent;
      }
      if (updates.loanOfficerSignatureAuditLog !== undefined) {
        dbUpdates.loan_officer_signature_audit_log = updates.loanOfficerSignatureAuditLog;
      }
      // Signature tracking field mappings
      if (updates.signatureSendCount !== undefined) {
        dbUpdates.signature_send_count = updates.signatureSendCount;
      }
      if (updates.lastSentWithSignatureAt !== undefined) {
        dbUpdates.last_sent_with_signature_at = updates.lastSentWithSignatureAt;
      }
      if (updates.signatureRecipientHistory !== undefined) {
        dbUpdates.signature_recipient_history = updates.signatureRecipientHistory;
      }

      // Signed PDF field mappings
      if (updates.signedPdfUrl !== undefined) {
        dbUpdates.signed_pdf_url = updates.signedPdfUrl;
      }
      if (updates.signedPdfGeneratedAt !== undefined) {
        dbUpdates.signed_pdf_generated_at = updates.signedPdfGeneratedAt;
      }

      // Always update the updated_at timestamp
      dbUpdates.updated_at = new Date().toISOString();

      const { data, error } = await supabase
        .from('pre_approvals')
        .update(dbUpdates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        logger.error('Error updating pre-approval:', error);
        throw new Error(`Failed to update pre-approval: ${error.message}`);
      }

      logger.info('Pre-approval updated successfully');
      return data ? this.convertFromDatabase(data) : null;
    } catch (error) {
      logger.error('Error in updatePreApproval:', error);
      throw error;
    }
  }

  static async deactivatePreApproval(id: string): Promise<void> {
    const expiredAt = new Date().toISOString();

    await this.updatePreApproval(id, {
      status: 'expired',
      expiredAt,
      expiredReason: 'manual'
    });

    await this.addActivity(id, {
      type: 'expired',
      description: 'Pre-approval manually deactivated',
      metadata: {
        expiredReason: 'manual',
        expiredAt
      }
    });
  }

  // Add activity tracking (stored in metadata for now)
  static async addActivity(
    idOrToken: string,
    activity: {
      type: 'created' | 'sent' | 'viewed' | 'modified' | 'accepted' | 'expired' | 'downloaded';
      description: string;
      metadata?: Record<string, string | number | boolean>;
    }
  ): Promise<void> {
    try {
      // Resolve token to UUID
      const preApprovalId = await this.resolvePreApprovalId(idOrToken);
      if (!preApprovalId) {
        logger.error('Pre-approval not found for activity tracking');
        return;
      }

      logger.info('Adding pre-approval activity:', { preApprovalId, activity });

      // For now, we'll store activities in the metadata field
      // In the future, this should be a separate activities table
      const { data: currentData, error: fetchError } = await supabase
        .from('pre_approvals')
        .select('metadata')
        .eq('id', preApprovalId)
        .single();

      if (fetchError) {
        logger.error('Error fetching pre-approval for activity:', fetchError);
        return;
      }

      const currentMetadata = currentData?.metadata || {};
      const activities = currentMetadata.activities || [];

      activities.push({
        ...activity,
        timestamp: new Date().toISOString(),
        id: Date.now().toString(36) + Math.random().toString(36).substr(2)
      });

      // Keep only the last 50 activities to prevent unlimited growth
      const recentActivities = activities.slice(-50);

      const { error: updateError } = await supabase
        .from('pre_approvals')
        .update({
          metadata: {
            ...currentMetadata,
            activities: recentActivities
          },
          updated_at: new Date().toISOString()
        })
        .eq('id', preApprovalId);

      if (updateError) {
        logger.error('Error updating pre-approval activity:', updateError);
      }
    } catch (error) {
      logger.error('Error in addActivity:', error);
      // Don't throw - activities are non-critical
    }
  }

  // Track view event using immutable event log
  static async trackView(idOrToken: string): Promise<void> {
    try {
      const { RecipientAccessService } = await import('./recipientAccessService');

      const success = await RecipientAccessService.trackRecipientView(
        idOrToken,
        typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        typeof document !== 'undefined' ? document.referrer : undefined
      );

      if (success) {
        logger.info('Successfully tracked pre-approval view');
        return;
      }

      logger.warn('Pre-approval view tracking rejected by server resolver', {
        tokenPrefix: idOrToken.substring(0, 8)
      });
    } catch (error) {
      logger.error('Error in trackView:', error);
      // Don't throw - view tracking is non-critical
    }
  }

  // Get accurate view count from event log
  static async getViewCount(preApprovalId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('pre_approval_view_events')
        .select('*', { count: 'exact', head: true })
        .eq('pre_approval_id', preApprovalId);

      if (error) {
        logger.error('Error fetching view count:', error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      logger.error('Error in getViewCount:', error);
      return 0;
    }
  }

  // Add modification record (stored in modifications array)
  static async addModification(
    idOrToken: string,
    modification: {
      id: string;
      fieldPath: string;
      oldValue: string;
      newValue: string;
      modifiedBy: 'borrower' | 'lender';
      modifiedAt: string;
      reason?: string;
    }
  ): Promise<void> {
    try {
      // Resolve token to UUID
      const preApprovalId = await this.resolvePreApprovalId(idOrToken);
      if (!preApprovalId) {
        logger.error('Pre-approval not found for modification tracking');
        return;
      }

      logger.info('Adding pre-approval modification:', { preApprovalId, modification });

      const { data: current, error: fetchError } = await supabase
        .from('pre_approvals')
        .select('modifications')
        .eq('id', preApprovalId)
        .single();

      if (fetchError) {
        logger.error('Error fetching pre-approval for modification:', fetchError);
        return;
      }

      const modifications = current?.modifications || [];
      modifications.push(modification);

      const { error: updateError } = await supabase
        .from('pre_approvals')
        .update({
          modifications,
          updated_at: new Date().toISOString()
        })
        .eq('id', preApprovalId);

      if (updateError) {
        logger.error('Error updating pre-approval modifications:', updateError);
      }

      // Also log as an activity (pass the UUID, not the original token)
      await this.addActivity(preApprovalId, {
        type: 'modified',
        description: `${modification.fieldPath} modified by ${modification.modifiedBy}`,
        metadata: {
          fieldPath: modification.fieldPath,
          modifiedBy: modification.modifiedBy
        }
      });
    } catch (error) {
      logger.error('Error in addModification:', error);
      // Don't throw - modifications are non-critical
    }
  }
}
